import { useEffect } from "react";
import { useParams } from "@/components/hooks/use-params";
import { useRoute } from "@/components/shared/router";
import { useBookingState } from "@/store/booking/useBookingState";
import type { ServiceName } from "@/types/service";

import { usePathname } from "./use-pathname";

export const bookingRoutes: Record<ServiceName, string[]> = {
  "home-cleaning": [
    "/service-detail/home-cleaning",
    "/booking/home-cleaning",
    "/booking/home-cleaning/select-slot",
    "/booking/home-cleaning/booking-info",
    "/booking/home-cleaning/confirmation",
    "/booking/home-cleaning/complete-booking",
  ],
  "deep-cleaning": [
    "/service-detail/deep-cleaning",
    "/booking/deep-cleaning",
    "/booking/deep-cleaning/select-slot",
    "/booking/deep-cleaning/booking-info",
    "/booking/deep-cleaning/confirmation",
    "/booking/deep-cleaning/complete-booking",
  ],
  "mattress-cleaning": [
    "/service-detail/mattress-cleaning",
    "/booking/mattress-cleaning",
    "/booking/mattress-cleaning/select-slot",
    "/booking/mattress-cleaning/booking-info",
    "/booking/mattress-cleaning/confirmation",
    "/booking/mattress-cleaning/complete-booking",
  ],

  "sofa-cleaning": [
    "/service-detail/sofa-cleaning",
    "/booking/sofa-cleaning",
    "/booking/sofa-cleaning/select-slot",
    "/booking/sofa-cleaning/booking-info",
    "/booking/sofa-cleaning/confirmation",
    "/booking/sofa-cleaning/complete-booking",
  ],
  "rug-cleaning": [
    "/service-detail/rug-cleaning",
    "/booking/rug-cleaning",
    "/booking/rug-cleaning/select-slot",
    "/booking/rug-cleaning/booking-info",
    "/booking/rug-cleaning/confirmation",
    "/booking/rug-cleaning/complete-booking",
  ],
  "curtain-cleaning": [
    "/service-detail/curtain-cleaning",
    "/booking/curtain-cleaning",
    "/booking/curtain-cleaning/select-slot",
    "/booking/curtain-cleaning/booking-info",
    "/booking/curtain-cleaning/confirmation",
    "/booking/curtain-cleaning/complete-booking",
  ],
  "pram-seat": [
    "/service-detail/pram-seat",
    "/booking/pram-seat",
    "/booking/pram-seat/select-slot",
    "/booking/pram-seat/booking-info",
    "/booking/pram-seat/confirmation",
    "/booking/pram-seat/complete-booking",
  ],
  "aircon-service": [
    "/select-service/aircon-service",
    "/service-detail/aircon-service",
    "/booking/aircon-service",
    "/booking/aircon-service/select-slot",
    "/booking/aircon-service/booking-info",
    "/booking/aircon-service/confirmation",
    "/booking/aircon-service/complete-booking",
  ],
  "home-beauty": [
    "/service-detail/home-beauty",
    "/booking/home-beauty",
    "/booking/home-beauty/select-slot",
    "/booking/home-beauty/booking-info",
    "/booking/home-beauty/confirmation",
    "/booking/home-beauty/complete-booking",
  ],
  "home-massage": [
    "/service-detail/home-massage",
    "/booking/home-massage",
    "/booking/home-massage/select-slot",
    "/booking/home-massage/booking-info",
    "/booking/home-massage/confirmation",
    "/booking/home-massage/complete-booking",
  ],
  "pet-grooming": [
    "/service-detail/pet-grooming",
    "/booking/pet-grooming",
    "/booking/pet-grooming/select-slot",
    "/booking/pet-grooming/booking-info",
    "/booking/pet-grooming/confirmation",
    "/booking/pet-grooming/complete-booking",
  ],
  "pet-sitting": [
    "/service-detail/pet-sitting",
    "/booking/pet-sitting",
    "/booking/pet-sitting/select-slot",
    "/booking/pet-sitting/booking-info",
    "/booking/pet-sitting/confirmation",
    "/booking/pet-sitting/complete-booking",
  ],
  lashes: [
    "/service-detail/lashes",
    "/booking/lashes",
    "/booking/lashes/select-slot",
    "/booking/lashes/booking-info",
    "/booking/lashes/confirmation",
    "/booking/lashes/complete-booking",
  ],
  "elder-care": [
    "/service-detail/elder-care",
    "/booking/elder-care",
    "/booking/elder-care/select-slot",
    "/booking/elder-care/booking-info",
    "/booking/elder-care/confirmation",
    "/booking/elder-care/complete-booking",
  ],
  // still un hold pending on vertical lead babysitter
  // babysitter: [
  //   "/service-detail/babysitter",
  //   "/booking/babysitter",
  //   "/booking/babysitter/select-slot",
  //   "/booking/babysitter/booking-info",
  //   "/booking/babysitter/confirmation",
  //   "/booking/babysitter/complete-booking",
  // ],
};
const freeViewPageIndex: Record<ServiceName, number> = {
  "home-cleaning": 1,
  "mattress-cleaning": 1,
  "sofa-cleaning": 1,
  "rug-cleaning": 1,
  "curtain-cleaning": 1,
  "pram-seat": 1,
  "aircon-service": 2,
  "home-beauty": 0,
  "home-massage": 0,
  "pet-grooming": 0,
  "pet-sitting": 0,
  "deep-cleaning": 1,
  lashes: 0,
  "elder-care": 0,
  // still un hold pending on vertical lead babysitter
  // babysitter: 0,
};
const freeViewPageRoutes: string[] = Object.entries(bookingRoutes).flatMap(
  ([serviceName, routes]) => {
    return routes.slice(0, freeViewPageIndex[serviceName as ServiceName] + 1);
  },
);
const firstPages = Object.values(bookingRoutes).map((routes) => routes[0]);

// TODO: This to handle the old booking routes from webflow project
// Remove this once the booking routes are updated and fixed in webflow project
const bookingAliases: Record<string, ServiceName> = {
  home: "home-cleaning",
  aircon: "aircon-service",
  beauty: "home-beauty",
  upholstery: "mattress-cleaning",
};

export const useBookingRouterInterceptor = () => {
  const { name } = useParams<{ name: ServiceName }>();
  const path = usePathname();
  const { replace } = useRoute();
  const {
    data: { currentPath },
    setBookingState,
  } = useBookingState();

  const returnToFirstPage = (serviceName: ServiceName) => {
    replace(bookingRoutes[serviceName][0]);
  };
  const returnToHomePage = () => {
    replace({
      pageKey: "homepage",
    });
  };

  useEffect(() => {
    const pathNotFound = !Object.values(bookingRoutes).flat().includes(path);

    if (pathNotFound && name && bookingAliases[name]) {
      replace(bookingRoutes[bookingAliases[name]][0]);
      return;
    }

    if (!name || pathNotFound) {
      returnToHomePage();
      return;
    }

    const canSkipServiceDetailPage = freeViewPageRoutes.includes(path);

    if (firstPages.includes(path) || canSkipServiceDetailPage) {
      setBookingState({
        currentPath: path,
        selectedServiceProfileId: null,
        creationReason: undefined,
        totalCleaner: 1,
      });
      return;
    }

    const stateStep = bookingRoutes[name].findIndex(
      (val) => val === currentPath,
    );
    const currentPageStep = bookingRoutes[name].findIndex(
      (val) => val === path,
    );

    if (
      stateStep < currentPageStep ||
      (stateStep > currentPageStep &&
        stateStep === bookingRoutes[name].length - 1)
    ) {
      returnToFirstPage(name);
    } else if (stateStep > currentPageStep) {
      setBookingState({
        currentPath: bookingRoutes[name][currentPageStep],
      });
    }
  }, []);
};

export const useBookingRoute = (): {
  forward(): void;
} => {
  const { replace, push } = useRoute();
  const path = usePathname();
  const { name = "home-cleaning" } = useParams<{ name: ServiceName }>();
  const { setBookingState } = useBookingState();

  const routes = bookingRoutes[name] ?? [];
  const index = routes.findIndex((val) => val === path);
  const isPathInvalid = index === -1;
  const isLastStep = index === routes.length - 1;

  const forward = () => {
    if (isPathInvalid) {
      replace({ pageKey: "homepage" });
      return;
    }

    if (isLastStep) {
      setBookingState({ currentPath: "" });
      replace({ pageKey: "visits" });
      return;
    }

    const nextPath = routes[index + 1];
    setBookingState({ currentPath: nextPath });
    push(nextPath);
  };

  return { forward };
};
